import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { publicEndpoints } from "structure/endpoints";
import WebsiteContent from "@lironsheli/website-content";
import { isEmbedContext, shouldHideCookieNotice } from "util/embedMode";
import { FullPageLoading } from "components/shared/Loading";
import { LimitReachedPage } from "components/LimitReachedPage";
import { NotFoundPage } from "components/NotFoundPage";
import { ErrorPage } from "components/ErrorPage";
import { CookieConsent } from "./CookieConsent";
import { setPageTitle, PAGE_TITLES } from "util/pageTitle";
import { useQuery } from "util/hooks";
import { AdoptWrapper, LiveWebsiteOverrides } from "./styles";
import { APP_URL, MIN_LOADING_TIME_MS } from "util/consts";
import { ExampleNotice } from "./ExampleNotice";
import { sanitizeProjectForms } from "util/sanitizeFormFields";
import { ErrorBoundary } from "components/shared/ErrorBoundary";

export const Website = () => {
  const { username, projectSlug } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isNotFound, setIsNotFound] = useState(false);
  const [isLimitReached, setIsLimitReached] = useState(false);
  const [data, setData] = useState(null);
  const query = useQuery();
  const editToken = query.get("edit");
  const embedMode = isEmbedContext(window.location.search);

  useEffect(() => {
    // Set loading title immediately
    setPageTitle(PAGE_TITLES.LOADING);

    setIsLoading(true);
    const loadingStartTime = Date.now();

    publicEndpoints
      .getPage(username, projectSlug, { edit: editToken })
      .then((res) => {
        if (res.error || res.exception || res.message === "Not found") {
          throw res;
        }
        setData(res);
      })
      .catch((e) => {
        if (e.code === "LR8") {
          setIsLimitReached(true);
        } else {
          setIsNotFound(true);
        }
      })
      .finally(() => {
        const elapsedTime = Date.now() - loadingStartTime;
        const remainingTime = MIN_LOADING_TIME_MS - elapsedTime;

        if (remainingTime > 0) {
          setTimeout(() => setIsLoading(false), remainingTime);
        } else {
          setIsLoading(false);
        }
      });

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (data?.name) {
      // Set dynamic page title based on the actual page data
      // Include username for context and better SEO
      const pageTitle = `${data.name} - ${username}`;
      setPageTitle(pageTitle);
    }
  }, [data, username]);

  if (isLoading) return <FullPageLoading />;

  if (isNotFound) return <NotFoundPage />;

  if (isLimitReached) return <LimitReachedPage />;

  if (!data) return <ErrorPage />;

  const hideCookieNotice = shouldHideCookieNotice(data.settings, embedMode);
  const language = data.settings?.language || data.settings?.locale || "en";

  return (
    <>
      {data.adopt && (
        <AdoptWrapper>
          <Link
            to={{
              pathname: `${APP_URL}adopt/${username}/${projectSlug}`,
            }}
            target="_blank"
          >
            Create a copy of this project on your account
          </Link>
        </AdoptWrapper>
      )}
      <ErrorBoundary label="live-website" message="This page couldn't be rendered">
        <WebsiteContent
          data={{
            ...data,
            forms: sanitizeProjectForms(data.forms),
          }}
          username={username}
          projectSlug={projectSlug}
          stopSubmit={username === "example"}
          embedMode={embedMode}
        />
      </ErrorBoundary>
      <LiveWebsiteOverrides />
      {username === "example" ? (
        <ExampleNotice projectSlug={`${projectSlug}`} />
      ) : (
        !hideCookieNotice && (
          <CookieConsent language={language} />
        )
      )}
    </>
  );
};
